import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { plural } from "../plural";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    IconButton as RebaseIconButton,
    TextField as RebaseTextField,
    Tooltip as RebaseTooltip,
    Typography as RebaseTypography,
    ExternalLinkIcon as RebaseExternalLinkIcon,
    cls,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";
import {
    Copy as RebaseCopyIcon,
    // `Link2`, not `Linkedin`: this package resolves to 0.344 locally and to the
    // 1.27 that @rebasepro/ui pins inside the Docker build, and 1.27 dropped the
    // brand icons. The dev server and `tsc` both saw 0.344 and were happy; the
    // production rollup build failed twelve minutes in with "Linkedin is not
    // exported". No brand icons here — see tests/lucide-icons.test.mjs.
    Link2 as RebaseLinkedinIcon,
    Mail as RebaseMailIcon,
    Search as RebaseSearchIcon,
    Trash2 as RebaseTrashIcon,
    Pencil as RebasePencilIcon
} from "lucide-react";

import { EmailComposer } from "./EmailComposer";
import { shortlistLabel as contactShortlistName } from "./CandidateShortlist";
import { callFunction, type MailboxStatus } from "./_email_shared";
import {
    buildOutreachDraft,
    compareContacts,
    contactLabel,
    contactNameKey,
    mailtoUrl,
    sinceLabel,
    EMAIL_STATUS_COLOR,
    EMAIL_STATUS_LABEL,
    ROLE_COLOR,
    ROLE_LABEL,
    emailStatusOf,
    roleOf,
    type ContactRow,
    type Employer,
    type RoleBucket
} from "./_prospects_shared";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const IconButton = RebaseIconButton as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Tooltip = RebaseTooltip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;
const CopyIcon = RebaseCopyIcon as React.ComponentType<any>;
const LinkedinIcon = RebaseLinkedinIcon as React.ComponentType<any>;
const MailIcon = RebaseMailIcon as React.ComponentType<any>;
const SearchIcon = RebaseSearchIcon as React.ComponentType<any>;
const TrashIcon = RebaseTrashIcon as React.ComponentType<any>;
const PencilIcon = RebasePencilIcon as React.ComponentType<any>;

/**
 * The people at one employer: who they are, how to reach them, where that came
 * from, and the button that goes and finds more.
 *
 * ── Every address says what it is ────────────────────────────────────
 *
 * A contact card never shows a bare email. It shows the address next to a chip
 * saying whether the employer published it, somebody typed it, or it was
 * derived from a name and a domain — and the derived ones carry their
 * alternatives, because the second pattern is right about as often as the first
 * and re-deriving it by hand is exactly the work this panel exists to remove.
 *
 * ── Writing ──────────────────────────────────────────────────────────
 *
 * "Escribir" opens the composer inline. It sends from the SustenTalent mailbox
 * and files the conversation against this employer and this person, so the reply
 * comes back into "Correo" and onto this account's history — rather than into a
 * Gmail account joined to the CRM by memory, which is what the `mailto:` this
 * replaced could only ever do.
 *
 * The activity entry is written by `functions/send-email` after SMTP has
 * answered, so the log says an email was sent only when one was. "Abrir en mi
 * correo" is the old path, kept for the message somebody would rather send under
 * their own name; it still logs only that an email was *started*, because that
 * is all it can honestly claim.
 */

interface Draft {
    full_name: string;
    role_title: string;
    email: string;
    linkedin_url: string;
    phone: string;
    notes: string;
}

const EMPTY_DRAFT: Draft = { full_name: "", role_title: "", email: "", linkedin_url: "", phone: "", notes: "" };

/**
 * A step asked for by the "Lo siguiente" card at the top of the pane.
 *
 * The search, the add form and the composer all live in here with their own
 * busy and error state, and lifting any of them out to be shared with a button
 * two hundred lines above would mean two copies of "is it searching". So the
 * card asks instead: `n` rises on every press, this panel scrolls into view and
 * performs it, and the result — "Buscando…", the alert, the composer — appears
 * where the operator was already going to look for it.
 */
export interface PanelAsk {
    action: "find" | "add" | "write";
    contactId?: string | null;
    n: number;
}

function draftFrom(contact: ContactRow): Draft {
    return {
        full_name: contact.full_name ?? "",
        role_title: contact.role_title ?? "",
        email: contact.email ?? "",
        linkedin_url: contact.linkedin_url ?? "",
        phone: contact.phone ?? "",
        notes: contact.notes ?? ""
    };
}

/**
 * Which conversation a hand-typed title belongs to.
 *
 * The browser's copy of `classifyRole` from the backend service, kept short on
 * purpose: it only has to be right about the words a person actually types into
 * a "Cargo" field, and being wrong costs a chip colour, not a row.
 */
function bucketForTitle(title: string): RoleBucket {
    const folded = title.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    if (/rrhh|recursos humanos|talento|talent|selecci|reclutam|people|hr\b|capital humano/.test(folded)) return "talent";
    if (/sosteni|sustenta|esg|ambient|climat|carbono|rsc|rse|csr|hseq/.test(folded)) return "sustainability";
    if (/ceo|coo|cfo|cto|director general|gerente general|fundador|founder|socio|presidente|country manager/.test(folded)) return "leadership";
    return "other";
}

export function ProspectContactsPanel({
    employer,
    client,
    ask,
    onChanged,
    onLog
}: {
    employer: Employer;
    client: any;
    ask?: PanelAsk | null;
    onChanged: () => Promise<void> | void;
    onLog: (kind: "email" | "call" | "note", body: string, contactId?: string | null) => Promise<void> | void;
}) {
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<Draft>(EMPTY_DRAFT);
    const [busy, setBusy] = useState(false);
    const [searching, setSearching] = useState(false);
    const [message, setMessage] = useState<{ tone: "info" | "error"; text: string } | null>(null);
    const [copied, setCopied] = useState<string | null>(null);
    /** The contact whose composer is open, if any. One at a time. */
    const [composeFor, setComposeFor] = useState<string | null>(null);
    const [mailbox, setMailbox] = useState<MailboxStatus | null>(null);

    const contacts = useMemo(() => [...employer.contacts].sort(compareContacts), [employer.contacts]);
    const data = client?.data;
    const root = useRef<HTMLDivElement | null>(null);

    // Whether this deployment can send at all. Asked once: the composer shows
    // the answer rather than failing on the button, so nobody writes six
    // paragraphs into a form that was never going to send them.
    useEffect(() => {
        callFunction<MailboxStatus>(client, "sync-inbox").then(setMailbox).catch(() => setMailbox(null));
    }, [client]);

    // Close the composer when the selection moves to another employer —
    // otherwise it stays open, addressed to somebody at the previous company.
    useEffect(() => { setComposeFor(null); }, [employer.key]);

    // ── Discovery ────────────────────────────────────────────────────
    const findContacts = useCallback(async (force: boolean) => {
        if (!client) return;
        setSearching(true);
        setMessage(null);
        try {
            const token = await client.resolveToken?.();
            const base = `${client.baseUrl ?? ""}${client.apiPath ?? "/api"}`;
            const res = await fetch(`${base}/functions/find-contacts`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    employer_key: employer.key,
                    employer_name: employer.name,
                    website: employer.website ?? undefined,
                    force
                })
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.error || `Error ${res.status}`);

            if (json.skipped) {
                setMessage({
                    tone: "info",
                    text: `Ya se buscó ${sinceLabel(json.synced_at)}: ${json.note ?? "sin resultado"}. Usa "Buscar de nuevo" para repetirla.`
                });
            } else {
                setMessage({
                    tone: "info",
                    text: json.created > 0
                        ? `${json.created} ${plural(json.created, "contacto nuevo", "contactos nuevos")} · ${json.note ?? ""}`
                        : `No se encontró a nadie nuevo · ${json.note ?? ""}`
                });
            }
            await onChanged();
        } catch (err: any) {
            setMessage({ tone: "error", text: err?.message || "No se pudo buscar personas" });
        } finally {
            setSearching(false);
        }
    }, [client, employer.key, employer.name, employer.website, onChanged]);

    /**
     * Perform whatever the "Lo siguiente" card asked for.
     *
     * Keyed on `ask.n` alone and on purpose: the same press twice has to search
     * twice, and re-running because `employer` changed identity would fire a
     * paid discovery call on every reload. Scrolls first — the answer to all
     * three of these appears in this panel, and a button that acts three hundred
     * pixels below the fold reads as a button that did nothing.
     */
    useEffect(() => {
        if (!ask) return;
        root.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (ask.action === "find") void findContacts(Boolean(employer.prospect?.contacts_synced_at));
        else if (ask.action === "add") setAdding(true);
        else if (ask.action === "write" && ask.contactId) setComposeFor(ask.contactId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ask?.n]);

    // ── Writes ───────────────────────────────────────────────────────
    const addContact = useCallback(async () => {
        if (!data) return;
        const name = draft.full_name.trim();
        const email = draft.email.trim().toLowerCase();
        const linkedin = draft.linkedin_url.trim();
        if (!name && !email && !linkedin) {
            setMessage({ tone: "error", text: "Hace falta al menos un nombre, un email o un perfil de LinkedIn." });
            return;
        }

        const nameKey = contactNameKey({ full_name: name, email, linkedin_url: linkedin });
        if (contacts.some(c => (c.name_key ?? "") === nameKey)) {
            setMessage({ tone: "error", text: "Esa persona ya está en la lista." });
            return;
        }

        setBusy(true);
        try {
            await data.collection("prospect-contacts").create({
                employer_key: employer.key,
                name_key: nameKey,
                kind: name ? "person" : (email ? "mailbox" : "person"),
                full_name: name || null,
                role_title: draft.role_title.trim() || null,
                role_bucket: bucketForTitle(draft.role_title),
                email: email || null,
                // Typed by a person, so it outranks anything discovery finds
                // later and is never overwritten by it.
                email_status: email ? "manual" : "none",
                email_alternatives: [],
                linkedin_url: linkedin || null,
                phone: draft.phone.trim() || null,
                source: "manual",
                notes: draft.notes.trim() || null
            });
            setDraft(EMPTY_DRAFT);
            setAdding(false);
            setMessage(null);
            await onChanged();
        } catch (err: any) {
            setMessage({ tone: "error", text: err?.message || "No se pudo guardar el contacto" });
        } finally {
            setBusy(false);
        }
    }, [data, draft, contacts, employer.key, onChanged]);

    const saveEdit = useCallback(async (contact: ContactRow) => {
        if (!data) return;
        setBusy(true);
        try {
            const email = editDraft.email.trim().toLowerCase();
            const patch: Record<string, unknown> = {
                full_name: editDraft.full_name.trim() || null,
                role_title: editDraft.role_title.trim() || null,
                role_bucket: bucketForTitle(editDraft.role_title),
                linkedin_url: editDraft.linkedin_url.trim() || null,
                phone: editDraft.phone.trim() || null,
                notes: editDraft.notes.trim() || null
            };
            // Only a changed address changes its provenance. Re-saving the card
            // after fixing a typo in the title must not relabel an address the
            // employer published as one we typed.
            if (email !== (contact.email ?? "").trim().toLowerCase()) {
                patch.email = email || null;
                patch.email_status = email ? "manual" : "none";
                patch.email_alternatives = [];
            }
            await data.collection("prospect-contacts").update(contact.id, patch);
            setEditingId(null);
            await onChanged();
        } catch (err: any) {
            setMessage({ tone: "error", text: err?.message || "No se pudo guardar" });
        } finally {
            setBusy(false);
        }
    }, [data, editDraft, onChanged]);

    const removeContact = useCallback(async (contact: ContactRow) => {
        if (!data) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm(`¿Eliminar a ${contactLabel(contact)}?`)) return;
        setBusy(true);
        try {
            await data.collection("prospect-contacts").delete(contact.id);
            await onChanged();
        } catch (err: any) {
            setMessage({ tone: "error", text: err?.message || "No se pudo eliminar" });
        } finally {
            setBusy(false);
        }
    }, [data, onChanged]);

    const copy = useCallback(async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(value);
            setTimeout(() => setCopied(current => (current === value ? null : current)), 1500);
        } catch {
            // Clipboard denied — the address is on screen and selectable.
        }
    }, []);

    /**
     * Open the composer on this contact.
     *
     * This used to be a `mailto:` that handed the operator's own mail client a
     * draft and logged that an email had been *started* — which was the honest
     * thing to record, because nothing after that point was visible to this
     * product. The reply came back to a Gmail account joined to the CRM by
     * memory, and the account's history said "empezó un email" forever.
     *
     * Now it sends from the SustenTalent mailbox and the thread lives in
     * "Correo", attached to this employer and this person. The activity entry is
     * written by `functions/send-email` after SMTP has answered, so the log says
     * an email was sent only when one was.
     *
     * `mailto:` stays available as "Abrir en mi correo", for the message somebody
     * would rather send under their own name from their own address.
     */
    const write = useCallback((contact: ContactRow) => {
        if (!contact.email) return;
        setComposeFor(current => (current === contact.id ? null : contact.id));
    }, []);

    const writeInOwnClient = useCallback((contact: ContactRow) => {
        if (!contact.email) return;
        const draftMail = buildOutreachDraft(employer, contact);
        window.open(mailtoUrl(contact.email, draftMail), "_self");
        void onLog("email", `Email a ${contactLabel(contact)} (${contact.email}) desde el correo propio: "${draftMail.subject}"`, contact.id);
    }, [employer, onLog]);

    const syncedAt = employer.prospect?.contacts_synced_at ?? null;

    return (
        <div className="flex flex-col gap-3 scroll-mt-4" ref={root}>
            <div className="flex items-center gap-2 flex-wrap">
                <Typography variant="subtitle2" component="h3" className="grow">
                    Personas ({contacts.length})
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={searching}
                    onClick={() => findContacts(Boolean(syncedAt))}
                >
                    <SearchIcon size={iconSize.smallest} />
                    {searching ? "Buscando…" : (syncedAt ? "Buscar de nuevo" : "Buscar personas")}
                </Button>
                <Button size="small" variant="text" onClick={() => setAdding(a => !a)}>
                    {adding ? "Cancelar" : "Añadir a mano"}
                </Button>
            </div>

            {/*
                "Nunca" and "hace un mes" are different situations and the screen
                has to tell them apart: without this line, an employer whose site
                names nobody looks exactly like one nobody has looked at, and
                gets searched again every time somebody opens the card.
            */}
            <Typography variant="body2" color="secondary">
                {syncedAt
                    ? `Última búsqueda ${sinceLabel(syncedAt)}${employer.prospect?.contacts_note ? ` · ${employer.prospect.contacts_note}` : ""}`
                    : "Nadie ha buscado personas en esta empresa todavía."}
            </Typography>

            {message && (
                <Alert color={message.tone === "error" ? "error" : "info"} size="small">
                    {message.text}
                </Alert>
            )}

            {adding && (
                <Card className={cls("p-3 flex flex-col gap-2", defaultBorderMixin)}>
                    <div className="flex gap-2 flex-wrap">
                        <TextField size="small" label="Nombre" value={draft.full_name} className="grow"
                            onChange={(e: any) => setDraft(d => ({ ...d, full_name: e.target.value }))} />
                        <TextField size="small" label="Cargo" value={draft.role_title} className="grow"
                            onChange={(e: any) => setDraft(d => ({ ...d, role_title: e.target.value }))} />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <TextField size="small" label="Email" value={draft.email} className="grow"
                            onChange={(e: any) => setDraft(d => ({ ...d, email: e.target.value }))} />
                        <TextField size="small" label="Teléfono" value={draft.phone}
                            onChange={(e: any) => setDraft(d => ({ ...d, phone: e.target.value }))} />
                    </div>
                    <TextField size="small" label="LinkedIn" value={draft.linkedin_url}
                        onChange={(e: any) => setDraft(d => ({ ...d, linkedin_url: e.target.value }))} />
                    <TextField size="small" label="Notas" value={draft.notes}
                        onChange={(e: any) => setDraft(d => ({ ...d, notes: e.target.value }))} />
                    <div>
                        <Button size="small" disabled={busy} onClick={addContact}>Guardar contacto</Button>
                    </div>
                </Card>
            )}

            {contacts.length === 0 && !adding && (
                <Card className="p-4">
                    <Typography variant="body1" color="secondary">
                        Sin personas todavía. "Buscar personas" lee las ofertas de esta empresa y su
                        web pública; lo que encuentre aparecerá aquí con su procedencia.
                    </Typography>
                </Card>
            )}

            {contacts.map(contact => {
                const role = roleOf(contact);
                const status = emailStatusOf(contact);
                const alternatives = (contact.email_alternatives ?? []).filter(Boolean);
                const editing = editingId === contact.id;

                return (
                    <Card key={contact.id} className="p-3 flex flex-col gap-2">
                        <div className="flex items-start gap-2 flex-wrap">
                            <div className="grow min-w-0">
                                <Typography variant="body2" className="font-medium truncate">
                                    {contactLabel(contact)}
                                    {contact.kind === "mailbox" && (
                                        <span className="ml-2 text-xs font-normal text-text-secondary dark:text-text-secondary-dark">
                                            buzón compartido
                                        </span>
                                    )}
                                </Typography>
                                {contact.role_title && (
                                    <Typography variant="body2" color="secondary" className="truncate">
                                        {contact.role_title}
                                    </Typography>
                                )}
                            </div>
                            <Chip size="small" colorScheme={ROLE_COLOR[role]}>{ROLE_LABEL[role]}</Chip>
                            <Tooltip title="Editar" asChild>
                                <IconButton size="smallest" aria-label="Editar contacto" onClick={() => {
                                    setEditingId(editing ? null : contact.id);
                                    setEditDraft(draftFrom(contact));
                                }}>
                                    <PencilIcon size={iconSize.smallest} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar" asChild>
                                <IconButton size="smallest" aria-label="Eliminar contacto" disabled={busy} onClick={() => removeContact(contact)}>
                                    <TrashIcon size={iconSize.smallest} />
                                </IconButton>
                            </Tooltip>
                        </div>

                        {!editing && (
                            <>
                                {contact.email ? (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-mono truncate">{contact.email}</span>
                                        <Chip size="small" colorScheme={EMAIL_STATUS_COLOR[status]}>
                                            {EMAIL_STATUS_LABEL[status]}
                                        </Chip>
                                        <Tooltip title={copied === contact.email ? "Copiado" : "Copiar"} asChild>
                                            <IconButton size="smallest" aria-label="Copiar email" onClick={() => copy(contact.email as string)}>
                                                <CopyIcon size={iconSize.smallest} />
                                            </IconButton>
                                        </Tooltip>
                                        <Button
                                            size="small"
                                            variant={composeFor === contact.id ? "filled" : "outlined"}
                                            onClick={() => write(contact)}
                                        >
                                            <MailIcon size={iconSize.smallest} />
                                            {composeFor === contact.id ? "Cancelar" : "Escribir"}
                                        </Button>
                                        {/* The old behaviour, kept for the message
                                            somebody would rather send under their
                                            own name from their own address. */}
                                        <Button size="small" variant="text" onClick={() => writeInOwnClient(contact)}>
                                            Abrir en mi correo
                                        </Button>
                                    </div>
                                ) : (
                                    <Typography variant="body2" color="secondary">
                                        Sin email. {contact.linkedin_url ? "Sólo perfil de LinkedIn." : "Añádelo a mano si lo consigues."}
                                    </Typography>
                                )}

                                {/*
                                    Shown for a guess and only for a guess: these
                                    are the same-likelihood spellings of one
                                    name, and offering them beside a published
                                    address would suggest the published one is
                                    also in doubt.
                                */}
                                {status === "pattern_guess" && alternatives.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Typography variant="body2" color="secondary">Otras formas posibles de su correo:</Typography>
                                        {alternatives.map(alt => (
                                            <button
                                                key={alt}
                                                type="button"
                                                onClick={() => copy(alt)}
                                                className="text-xs font-mono text-primary hover:underline"
                                                title="Copiar"
                                            >
                                                {copied === alt ? "copiado" : alt}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <div className="flex items-center gap-4 flex-wrap">
                                    {contact.linkedin_url && (
                                        <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                                            <LinkedinIcon size={12} /> LinkedIn
                                        </a>
                                    )}
                                    {contact.phone && (
                                        <span className="text-xs text-text-secondary dark:text-text-secondary-dark">{contact.phone}</span>
                                    )}
                                    {contact.source_url && (
                                        <a href={contact.source_url} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                                            de dónde salió <ExternalLinkIcon size={12} />
                                        </a>
                                    )}
                                </div>

                                {contact.notes && (
                                    <Typography variant="body2" color="secondary">{contact.notes}</Typography>
                                )}

                                {/*
                                    Sends from the SustenTalent mailbox and files
                                    the thread against this employer and this
                                    person — so the reply comes back into
                                    "Correo" and onto this account's history,
                                    rather than into somebody's Gmail.
                                */}
                                {composeFor === contact.id && contact.email && (
                                    <EmailComposer
                                        client={client}
                                        compact
                                        canSend={mailbox?.sending !== false}
                                        target={{
                                            to: [contact.email],
                                            subject: "",
                                            threadId: null,
                                            link: {
                                                entity_type: "prospect",
                                                employer_key: employer.key,
                                                contact_id: contact.id
                                            },
                                            draftContext: {
                                                entity_type: "prospect",
                                                employer_key: employer.key,
                                                contact_id: contact.id,
                                                tone: "primer_contacto"
                                            },
                                            // The one composer in the product
                                            // where naming candidates is the
                                            // point: this is the outreach email
                                            // that has always promised them.
                                            allowShortlist: true,
                                            // Presenting needs a company record
                                            // to present to; most prospects
                                            // have none, and then the button
                                            // is simply not offered.
                                            presentTo: employer.companyId
                                                ? { id: employer.companyId, name: employer.name }
                                                : null,
                                            recipientName: contact.full_name ?? null,
                                            employerName: employer.name
                                        }}
                                        onShortlistSent={candidates => onLog(
                                            "note",
                                            `Se les nombró: ${candidates.map(contactShortlistName).join(", ")}.`,
                                            contact.id
                                        )}
                                        onShortlistPresented={candidates => onLog(
                                            "note",
                                            `Se les presentó en su panel: ${candidates.map(contactShortlistName).join(", ")}.`,
                                            contact.id
                                        )}
                                        onSent={async () => {
                                            setComposeFor(null);
                                            setMessage({ tone: "info", text: `Enviado a ${contactLabel(contact)}.` });
                                            // The activity entry is written by
                                            // the send function itself, after
                                            // SMTP answered — this only refreshes
                                            // what the screen is showing.
                                            await onChanged();
                                        }}
                                        onCancel={() => setComposeFor(null)}
                                    />
                                )}
                            </>
                        )}

                        {editing && (
                            <div className="flex flex-col gap-2 pt-1">
                                <div className="flex gap-2 flex-wrap">
                                    <TextField size="small" label="Nombre" value={editDraft.full_name} className="grow"
                                        onChange={(e: any) => setEditDraft(d => ({ ...d, full_name: e.target.value }))} />
                                    <TextField size="small" label="Cargo" value={editDraft.role_title} className="grow"
                                        onChange={(e: any) => setEditDraft(d => ({ ...d, role_title: e.target.value }))} />
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <TextField size="small" label="Email" value={editDraft.email} className="grow"
                                        onChange={(e: any) => setEditDraft(d => ({ ...d, email: e.target.value }))} />
                                    <TextField size="small" label="Teléfono" value={editDraft.phone}
                                        onChange={(e: any) => setEditDraft(d => ({ ...d, phone: e.target.value }))} />
                                </div>
                                <TextField size="small" label="LinkedIn" value={editDraft.linkedin_url}
                                    onChange={(e: any) => setEditDraft(d => ({ ...d, linkedin_url: e.target.value }))} />
                                <TextField size="small" label="Notas" value={editDraft.notes}
                                    onChange={(e: any) => setEditDraft(d => ({ ...d, notes: e.target.value }))} />
                                <div className="flex gap-2">
                                    <Button size="small" disabled={busy} onClick={() => saveEdit(contact)}>Guardar</Button>
                                    <Button size="small" variant="text" onClick={() => setEditingId(null)}>Cancelar</Button>
                                </div>
                            </div>
                        )}
                    </Card>
                );
            })}
        </div>
    );
}
